import { ERoomColoringCategoryId, ERoomColoringSubcategoryId } from "./roomColoringConstants";
import {
  pickColorFromRangedSpecs,
  pickNoDataColor,
  roomHasStandardRoom,
} from "./roomColoringHelpers";
import {
  BLUE_GRADIENTS,
  DESIGN_DEVIATION_STATUS_COLORS,
  DR_STATUS_COLORS,
  GRAY,
  MMI_COLORS,
  YELLOW_GRADIENTS,
} from "./roomColoringPalettes";
import {
  ROOM_COLORING_IDS,
  IRoomColoring,
  IGetColorArgs,
  IPolygonColors,
} from "./roomColoringTypes";

export const ROOM_COLORINGS: IRoomColoring[] = [
  {
    id: ROOM_COLORING_IDS.SPACE_BELONG_TO_STANDARD_ROOM,
    title: "Standardrom",
    categoryId: ERoomColoringCategoryId.DrofusEgenskaper,
    subcategoryId: ERoomColoringSubcategoryId.DrofusRomData,
    explanation: {
      shortNo: "Fargelegger rom etter om det er koblet til en standardromtype i dRofus.",
      longNo:
        "Grønn betyr at rommet har en standardromkobling. Hvit betyr at rommet ikke er koblet til standardrom.",
      docKey: "colorfilters/standardrom",
    },
    order: 10,
    tags: ["drofus", "standardrom"],
    colorSpecs: [
      {
        id: "spaceBelongToStandardRoom",
        fillColor: "#84f884",
        explanation: "Rommet er tilknyttet en standardromtype",
      },
      {
        id: "spaceNotBelongToStandardRoom",
        fillColor: "#ffffff",
        explanation: "Rommet er ikke tilknyttet en standardromtype",
      },
    ],
    getPolygonColors: (args: IGetColorArgs): IPolygonColors => ({
      fill: roomHasStandardRoom(args),
    }),
  },
  {
    id: ROOM_COLORING_IDS.SUPPLY_AIR_FLOW,
    title: "Tilluft",
    categoryId: ERoomColoringCategoryId.MepLaster,
    subcategoryId: ERoomColoringSubcategoryId.Luftmengder,
    explanation: {
      shortNo: "Fargelegger rom etter tilluftsmengde (m3/h) i intervaller.",
      longNo:
        "Brukes for å få rask oversikt over rom med høy/lav tilluft. 0 eller manglende verdi vises som grå (Ingen data).",
      docKey: "colorfilters/tilluft",
    },
    trendKeyPath: "supplyAirFlow",
    order: 20,
    tags: ["ventilasjon", "tilluft", "laster"],
    colorSpecs: [
      {
        id: "VERY_LOW",
        fillColor: BLUE_GRADIENTS.VERY_LIGHT,
        explanation: "1-50 m3/h",
        range: { minInclusive: 1, maxInclusive: 50 },
      },
      {
        id: "LOW",
        fillColor: BLUE_GRADIENTS.LIGHT,
        explanation: "51-100 m3/h",
        range: { minInclusive: 51, maxInclusive: 100 },
      },
      {
        id: "MEDIUM",
        fillColor: BLUE_GRADIENTS.MEDIUM,
        explanation: "101-200 m3/h",
        range: { minInclusive: 101, maxInclusive: 200 },
      },
      {
        id: "HIGH",
        fillColor: BLUE_GRADIENTS.DARK,
        explanation: "201-300 m3/h",
        range: { minInclusive: 201, maxInclusive: 300 },
      },
      {
        id: "VERY_HIGH",
        fillColor: BLUE_GRADIENTS.VERY_DARK,
        explanation: ">300 m3/h",
        range: { minExclusive: 300 },
      },
      { id: "NO_DATA", fillColor: GRAY, explanation: "Ingen data" },
    ],
    getPolygonColors: (args: IGetColorArgs): IPolygonColors => {
      const noData = pickNoDataColor(args, GRAY);
      return {
        fill: pickColorFromRangedSpecs(args.room?.supplyAirFlow, args.colorSpecs, noData, true),
      };
    },
  },
  {
    id: ROOM_COLORING_IDS.RETURN_AIR_FLOW,
    title: "Avtrekk",
    categoryId: ERoomColoringCategoryId.MepLaster,
    subcategoryId: ERoomColoringSubcategoryId.Luftmengder,
    explanation: {
      shortNo: "Fargelegger rom etter avtrekksmengde (m3/h) i intervaller.",
      longNo:
        "Brukes for å få rask oversikt over rom med høy/lav avtrekk. 0 eller manglende verdi vises som grå (Ingen data).",
      docKey: "colorfilters/avtrekk",
    },
    trendKeyPath: "returnAirFlow",
    order: 30,
    tags: ["ventilasjon", "avtrekk", "laster"],
    colorSpecs: [
      {
        id: "VERY_LOW",
        fillColor: YELLOW_GRADIENTS.VERY_LIGHT,
        explanation: "1-50 m3/h",
        range: { minInclusive: 1, maxInclusive: 50 },
      },
      {
        id: "LOW",
        fillColor: YELLOW_GRADIENTS.LIGHT,
        explanation: "51-100 m3/h",
        range: { minInclusive: 51, maxInclusive: 100 },
      },
      {
        id: "MEDIUM",
        fillColor: YELLOW_GRADIENTS.MEDIUM,
        explanation: "101-200 m3/h",
        range: { minInclusive: 101, maxInclusive: 200 },
      },
      {
        id: "HIGH",
        fillColor: YELLOW_GRADIENTS.DARK,
        explanation: "201-300 m3/h",
        range: { minInclusive: 201, maxInclusive: 300 },
      },
      {
        id: "VERY_HIGH",
        fillColor: YELLOW_GRADIENTS.VERY_DARK,
        explanation: ">300 m3/h",
        range: { minExclusive: 300 },
      },
      { id: "NO_DATA", fillColor: GRAY, explanation: "0 eller Ingen data" },
    ],
    getPolygonColors: (args: IGetColorArgs): IPolygonColors => {
      const noData = pickNoDataColor(args, GRAY);
      return {
        fill: pickColorFromRangedSpecs(args.room?.returnAirFlow, args.colorSpecs, noData, true),
      };
    },
  },
  {
    id: ROOM_COLORING_IDS.MMI,
    title: "MMI",
    categoryId: ERoomColoringCategoryId.IfcEgenskaper,
    subcategoryId: ERoomColoringSubcategoryId.IfcPsets,
    explanation: {
      shortNo: "Fargelegger rom etter MMI-kode (hvis tilgjengelig i IFC/ekstra IFC-info).",
      longNo:
        "MMI brukes som en klassifisering/merking. Manglende eller ukjent MMI vises som grå (Ingen data).",
      docKey: "colorfilters/mmi",
    },
    trendKeyPath: "extraIfcInfo.mmi",
    order: 40,
    tags: ["ifc", "mmi"],
    colorSpecs: [
      ...Object.entries(MMI_COLORS)
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([code, hex]) => ({ id: code, fillColor: hex, explanation: `MMI ${code}` })),
      { id: "NO_DATA", fillColor: GRAY, explanation: "Ingen data / ukjent MMI" },
    ],
    getPolygonColors: (args: IGetColorArgs): IPolygonColors => {
      const raw = (args.room as any)?.extraIfcInfo?.mmi;
      const code =
        typeof raw === "number" ? raw : typeof raw === "string" ? Number(raw.trim()) : undefined;
      const fill =
        typeof code === "number" && Number.isFinite(code) && (MMI_COLORS as any)[code]
          ? (MMI_COLORS as any)[code]
          : pickNoDataColor(args, GRAY);
      return { fill };
    },
  },
  {
    id: ROOM_COLORING_IDS.CEILING_MMI,
    title: "Himling MMI",
    categoryId: ERoomColoringCategoryId.IfcEgenskaper,
    subcategoryId: ERoomColoringSubcategoryId.IfcPsets,
    explanation: {
      shortNo:
        "Fargelegger rom etter MMI-koden på himling, når den er utledet fra rommets MCBIM-items.",
      longNo:
        "Brukes for å se hvilken himlingsklassifisering rommet har. Manglende eller ukjent himlings-MMI vises som grå (Ingen data).",
      docKey: "colorfilters/ceiling-mmi",
    },
    trendKeyPath: "extraIfcInfo.ceilingMmi",
    order: 45,
    tags: ["ifc", "mmi", "himling", "ceiling"],
    colorSpecs: [
      ...Object.entries(MMI_COLORS)
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([code, hex]) => ({ id: code, fillColor: hex, explanation: `Himling MMI ${code}` })),
      { id: "NO_DATA", fillColor: GRAY, explanation: "Ingen data / ukjent himlings-MMI" },
    ],
    getPolygonColors: (args: IGetColorArgs): IPolygonColors => {
      const raw = (args.room as any)?.extraIfcInfo?.ceilingMmi;
      const code =
        typeof raw === "number" ? raw : typeof raw === "string" ? Number(raw.trim()) : undefined;
      const fill =
        typeof code === "number" && Number.isFinite(code) && (MMI_COLORS as any)[code]
          ? (MMI_COLORS as any)[code]
          : pickNoDataColor(args, GRAY);
      return { fill };
    },
  },
  {
    id: ROOM_COLORING_IDS.DR_STATUS,
    title: "dR-status",
    categoryId: ERoomColoringCategoryId.DrofusEgenskaper,
    subcategoryId: ERoomColoringSubcategoryId.DrofusKlassifisering,
    explanation: {
      shortNo: "Fargelegger rom etter dR-status (dR0-dR4).",
      longNo:
        "dR-status kan brukes til å indikere rommets modenhet/kravstatus. Manglende status vises som grå (Ingen status).",
      docKey: "colorfilters/dr-status",
    },
    trendKeyPath: "drStatus",
    order: 50,
    tags: ["drofus", "dr", "status"],
    colorSpecs: [
      { id: "dR0", fillColor: DR_STATUS_COLORS.dR0, explanation: "dR0" },
      { id: "dR1", fillColor: DR_STATUS_COLORS.dR1, explanation: "dR1" },
      { id: "dR2", fillColor: DR_STATUS_COLORS.dR2, explanation: "dR2" },
      { id: "dR2A", fillColor: DR_STATUS_COLORS.dR2A, explanation: "dR2A" },
      { id: "dR3", fillColor: DR_STATUS_COLORS.dR3, explanation: "dR3" },
      { id: "dR3A", fillColor: DR_STATUS_COLORS.dR3A, explanation: "dR3A" },
      { id: "dR4", fillColor: DR_STATUS_COLORS.dR4, explanation: "dR4" },
      { id: "NO_DATA", fillColor: GRAY, explanation: "Ingen status" },
    ],
    getPolygonColors: (args: IGetColorArgs): IPolygonColors => {
      const key = typeof args.room?.drStatus === "string" ? args.room.drStatus.trim() : "";
      return { fill: DR_STATUS_COLORS[key] ?? pickNoDataColor(args, GRAY) };
    },
  },
  {
    id: ROOM_COLORING_IDS.DESIGN_DEVIATION_STATUS,
    title: "Avvikende prosjektering",
    categoryId: ERoomColoringCategoryId.DrofusEgenskaper,
    subcategoryId: ERoomColoringSubcategoryId.DrofusKlassifisering,
    explanation: {
      shortNo: "Fargelegger rom etter designDeviationStatus (01-03).",
      longNo:
        "Brukes for å visualisere rom med registrert avvikende prosjektering. Manglende verdi (null/ingen) vises som grå.",
      docKey: "colorfilters/design-deviation-status",
    },
    trendKeyPath: "designDeviationStatus",
    order: 60,
    tags: ["drofus", "design", "avvik"],
    colorSpecs: [
      { id: "01", fillColor: DESIGN_DEVIATION_STATUS_COLORS["01"], explanation: "Status 01" },
      { id: "02", fillColor: DESIGN_DEVIATION_STATUS_COLORS["02"], explanation: "Status 02" },
      { id: "03", fillColor: DESIGN_DEVIATION_STATUS_COLORS["03"], explanation: "Status 03" },
      { id: "04", fillColor: DESIGN_DEVIATION_STATUS_COLORS["04"], explanation: "Status 04" },
      { id: "05", fillColor: DESIGN_DEVIATION_STATUS_COLORS["05"], explanation: "Status 05" },
      { id: "NO_DATA", fillColor: GRAY, explanation: "Ingen status" },
    ],
    getPolygonColors: (args: IGetColorArgs): IPolygonColors => {
      const raw =
        typeof args.room?.designDeviationStatus === "string"
          ? args.room.designDeviationStatus.trim()
          : null;

      if (raw == null || !(raw in DESIGN_DEVIATION_STATUS_COLORS)) {
        return { fill: pickNoDataColor(args, GRAY) };
      }

      return {
        fill: DESIGN_DEVIATION_STATUS_COLORS[raw as "01" | "02" | "03" | "04" | "05"],
      };
    },
  },
  {
    id: ROOM_COLORING_IDS.ROOM_NUMBERING,
    title: "Romnummerering",
    categoryId: ERoomColoringCategoryId.Annet,
    subcategoryId: ERoomColoringSubcategoryId.Annet,
    explanation: {
      shortNo:
        "Fargelegger rom etter status for romnummerering. Grønn betyr godkjent, rød betyr at rommet mangler både forslag og manuell verdi.",
      longNo:
        "Brukes for å finne rom som har godkjent romnummerering, og rom som ikke har fått foreslått romnummer og som heller ikke har en manuelt angitt verdi.",
      docKey: "colorfilters/room-numbering",
    },
    order: 70,
    tags: ["romnummerering", "room-numbering"],
    colorSpecs: [
      {
        id: "APPROVED",
        fillColor: "#51cf66",
        explanation: "Godkjent romnummerering",
      },
      {
        id: "MISSING",
        fillColor: "#ff6b6b",
        explanation: "Mangler forslag og manuell verdi",
      },
      {
        id: "MANUAL",
        fillColor: "#ffec99",
        explanation: "Manuell verdi satt",
      },
      {
        id: "HAS_VALUE",
        fillColor: "#dee2e6",
        explanation: "Har forslag eller manuell verdi",
      },
    ],
    getPolygonColors: (args: IGetColorArgs): IPolygonColors => {
      const isApproved = args.room?.roomNumbering?.approval != null;
      const suggested = args.room?.roomNumbering?.suggested?.trim();
      const value = args.room?.roomNumbering?.value?.trim();
      const hasSuggested = Boolean(suggested && suggested !== "-");
      const hasValue = Boolean(value && value !== "-");

      if (isApproved) {
        return { fill: "#51cf66" };
      }

      if (hasValue) {
        return { fill: "#ffec99" };
      }

      if (!hasSuggested) {
        return { fill: "#ff6b6b" };
      }

      return { fill: "#dee2e6" };
    },
  },
  {
    id: ROOM_COLORING_IDS.CEILING_COMMENTS,
    title: "Himlingskommentarer",
    categoryId: ERoomColoringCategoryId.Annet,
    subcategoryId: ERoomColoringSubcategoryId.Annet,
    explanation: {
      shortNo: "Fargelegger rom som har kommentarer i himlingsmodulen.",
      longNo:
        "Oransje betyr at rommet har minst én himlingskommentar. Hvit betyr at rommet ikke har himlingskommentarer.",
      docKey: "colorfilters/ceiling-comments",
    },
    order: 80,
    tags: ["himling", "kommentar", "ceiling"],
    colorSpecs: [
      {
        id: "HAS_CEILING_COMMENTS",
        fillColor: "#ffa94d",
        explanation: "Har himlingskommentar",
      },
      {
        id: "NO_CEILING_COMMENTS",
        fillColor: "#ffffff",
        explanation: "Ingen himlingskommentar",
      },
    ],
    getPolygonColors: (args: IGetColorArgs): IPolygonColors => {
      const hasComments = (args.room?.ceilingComments?.length ?? 0) > 0;
      return { fill: hasComments ? "#ffa94d" : "#ffffff" };
    },
  },
];
